import { useEffect, useRef, useState } from "react";
import type { CaseView, PackView } from "../api";
import { api } from "../api";
import { CraigFace } from "./CraigFace";

/* ═══════════════════════════════════════════════════════════════════════
   The receipt drop.

   One step, right after the talking is done, and deliberately dumb.
   Everything goes into one pile in any order — no sorting, no naming, no
   categories. The reviewer works through it, which is the right division
   of labour: they are the ones who can tell a capital purchase from a
   repair, and they are accountable for the call either way.

   It opens itself, because "there's a panel on the right you should now
   use" is a sentence people don't act on.
   ═══════════════════════════════════════════════════════════════════════ */

/** Matches the API's own downscale threshold. */
const MAX_EDGE = 1568;

/**
 * Shrinks a photo before it goes anywhere.
 *
 * A modern phone camera produces 4–9 MB files, and a customer adding a
 * dozen receipts over mobile data would otherwise sit watching a progress
 * bar for minutes. Downscaling to 1568px on the long edge keeps receipt
 * text legible at a fraction of the bytes. PDFs pass through untouched.
 */
async function shrink(file: File): Promise<File> {
  if (!file.type.startsWith("image/")) return file;
  // HEIC can't be decoded by canvas in most browsers — send it as-is and
  // let the server keep the original.
  if (/heic|heif/i.test(file.type)) return file;

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size < 1_500_000) {
      bitmap.close();
      return file;
    }

    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();

    const blob = await new Promise<Blob | null>((res) =>
      // 0.82 keeps receipt text readable while cutting most of the weight.
      canvas.toBlob(res, "image/jpeg", 0.82),
    );
    if (!blob || blob.size >= file.size) return file;

    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    // Anything unexpected — send the original rather than lose the receipt.
    return file;
  }
}

interface Preview {
  name: string;
  url?: string;
  isImage: boolean;
}

export function DropZone({
  open,
  kase,
  pack,
  firstName,
  onClose,
  onUpdated,
}: {
  open: boolean;
  kase: CaseView;
  pack: PackView;
  firstName: string;
  onClose(): void;
  onUpdated(c: CaseView): void;
}) {
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [previews, setPreviews] = useState<Preview[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Object URLs are a memory leak if you don't hand them back.
  useEffect(
    () => () => previews.forEach((p) => p.url && URL.revokeObjectURL(p.url)),
    [previews],
  );

  if (!open) return null;

  async function add(list: FileList | null) {
    const files = Array.from(list ?? []);
    if (!files.length) return;
    setBusy(true);
    setProblems([]);

    try {
      const shrunk = await Promise.all(files.map(shrink));
      const result = await api.uploadDocuments(kase.id, shrunk);

      setPreviews((prev) => [
        ...prev,
        ...shrunk
          .filter((f) => result.added.includes(f.name))
          .map((f) => ({
            name: f.name,
            isImage: f.type.startsWith("image/"),
            ...(f.type.startsWith("image/") ? { url: URL.createObjectURL(f) } : {}),
          })),
      ]);

      if (result.rejected.length) {
        setProblems(result.rejected.map((r) => `${r.name} — ${r.why}`));
      }
      onUpdated(result.case);
    } catch (e) {
      setProblems([(e as Error).message]);
    } finally {
      setBusy(false);
    }
  }

  const count = kase.documents.length;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center fade-in"
      style={{ background: "rgba(20,20,18,.45)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add your receipts"
        className="card w-full sm:w-auto scroll-y"
        style={{ maxWidth: 560, maxHeight: "92vh", borderRadius: "16px 16px 0 0" }}
      >
        <div className="px-5 pt-5 pb-4 safe-b space-y-4">
          <div className="flex items-start gap-3">
            <CraigFace size={38} />
            <div className="min-w-0">
              <p style={{ fontWeight: 650, fontSize: 15 }}>
                That's all the questions done{firstName ? `, ${firstName}` : ""} — nice work.
              </p>
              <p style={{ fontSize: 14, color: "var(--ink-2)", marginTop: 4 }}>
                {pack.documents.prompt}
              </p>
            </div>
          </div>

          <button
            type="button"
            onDragOver={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              void add(e.dataTransfer?.files ?? null);
            }}
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="w-full flex flex-col items-center justify-center text-center px-4 py-9"
            style={{
              border: `2px dashed ${over ? "var(--accent)" : "var(--line-2)"}`,
              borderRadius: "var(--radius-lg)",
              background: over ? "var(--accent-tint)" : "var(--surface-2)",
              cursor: busy ? "wait" : "pointer",
            }}
          >
            <span style={{ fontSize: 26 }} aria-hidden="true">
              📄
            </span>
            <span style={{ fontWeight: 600, marginTop: 8 }}>
              {busy ? "Adding…" : "Drop your files here"}
            </span>
            <span style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 2 }}>
              or tap to take a photo or choose files
            </span>
            <span style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 8 }}>
              Photos, screenshots and PDFs all work
            </span>
          </button>

          <input
            ref={inputRef}
            type="file"
            accept={pack.documents.accept}
            multiple
            // `capture` makes the camera the default on a phone, which is
            // what most people want when the word "receipt" is on screen.
            capture="environment"
            onChange={(e) => {
              void add(e.target.files);
              e.target.value = "";
            }}
            style={{ display: "none" }}
          />

          {problems.length > 0 && (
            <ul
              style={{
                fontSize: 13,
                color: "var(--warn)",
                background: "var(--warn-tint)",
                borderRadius: "var(--radius)",
                padding: "0.6rem 0.8rem",
                margin: 0,
                listStyle: "none",
              }}
            >
              {problems.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          )}

          {count > 0 && (
            <div>
              <p style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 8 }}>
                {count} added
              </p>
              <div className="flex flex-wrap gap-2">
                {previews.map((p, i) => (
                  <div key={i} style={{ width: 68 }}>
                    {p.isImage && p.url ? (
                      <img
                        src={p.url}
                        alt=""
                        style={{
                          width: 68,
                          height: 68,
                          objectFit: "cover",
                          borderRadius: 8,
                          border: "1px solid var(--line)",
                        }}
                      />
                    ) : (
                      <div
                        className="flex items-center justify-center"
                        style={{
                          width: 68,
                          height: 68,
                          borderRadius: 8,
                          border: "1px solid var(--line)",
                          background: "var(--surface-2)",
                          fontSize: 20,
                        }}
                      >
                        📕
                      </div>
                    )}
                    <div
                      className="truncate"
                      style={{ fontSize: 10.5, color: "var(--ink-3)", marginTop: 3 }}
                    >
                      {p.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p style={{ fontSize: 13, color: "var(--ink-2)" }}>
            Can't find something? Don't worry about it — your figures are already written down,
            and your {pack.copy.reviewerNoun} will tell you if they need anything else.
          </p>

          <div className="flex items-center gap-3">
            <button type="button" onClick={onClose} disabled={busy} className="btn btn-primary">
              {count ? "Done — that's all of them" : "I'll do this later"}
            </button>
          </div>

          {pack.documents.never.length > 0 && (
            <p style={{ fontSize: 12, color: "var(--ink-3)" }}>
              🔒 {pack.documents.never[0]}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
